import { cn } from '@sohanemon/utils';
import { Iconify } from '@sohanemon/utils/components';
import { m } from '@sohanscript/i18n';
import { THEME_DEFINITIONS, THEME_MODES } from '@sohanscript/theme';
import {
	usePreviewTheme,
	useResolvedTheme,
	useSetThemeMode,
	useThemeMode,
} from '@sohanscript/theme/store';
import * as React from 'react';
import { ThemePicker } from './theme-picker';

type ThemeSwitcherProps = React.ComponentProps<'div'> & {
	compact?: boolean;
};

export function ThemeSwitcher({
	compact = false,
	className,
	...props
}: ThemeSwitcherProps) {
	const mode = useThemeMode();
	const resolved = useResolvedTheme();
	const previewTheme = usePreviewTheme();
	const setMode = useSetThemeMode();
	const [isOpen, setIsOpen] = React.useState(false);
	const committedMode = React.useRef(mode);
	const [selectedIndex, setSelectedIndex] = React.useState(() =>
		Math.max(0, THEME_MODES.indexOf(mode)),
	);
	const selectedMode = THEME_MODES[selectedIndex] ?? mode;

	React.useEffect(() => {
		function handleShortcut(event: KeyboardEvent) {
			if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 't') {
				event.preventDefault();
				setIsOpen((current) => {
					if (current) {
						previewTheme(committedMode.current);
					} else {
						committedMode.current = mode;
						setSelectedIndex(Math.max(0, THEME_MODES.indexOf(mode)));
					}
					return !current;
				});
			}
		}
		document.addEventListener('keydown', handleShortcut);
		return () => document.removeEventListener('keydown', handleShortcut);
	}, [mode, previewTheme]);

	function openPicker() {
		committedMode.current = mode;
		setSelectedIndex(Math.max(0, THEME_MODES.indexOf(mode)));
		setIsOpen(true);
	}

	function cancelPicker() {
		previewTheme(committedMode.current);
		setIsOpen(false);
	}

	function selectTheme(index: number) {
		const nextIndex = (index + THEME_MODES.length) % THEME_MODES.length;
		setSelectedIndex(nextIndex);
		previewTheme(THEME_MODES[nextIndex]);
	}

	function commitTheme() {
		setMode(selectedMode, () => setIsOpen(false));
	}

	return (
		<div className={cn('flex items-center', className)} {...props}>
			<button
				type="button"
				aria-label={m.theme_label()}
				onClick={openPicker}
				className="group inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-secondary outline-none transition-colors hover:text-primary focus-visible:ring-2 focus-visible:ring-primary"
			>
				<Iconify icon={THEME_DEFINITIONS[resolved].icon} className="size-4" />
				<span
					className={cn(
						'font-mono text-xs uppercase tracking-wide',
						compact && 'hidden xl:inline',
					)}
				>
					{m.theme_label()}
				</span>
			</button>
			<ThemePicker
				mode={selectedMode}
				open={isOpen}
				selectedIndex={selectedIndex}
				onCancel={cancelPicker}
				onCommit={commitTheme}
				onSelect={selectTheme}
			/>
		</div>
	);
}
